import React, { useContext, useEffect, useState, useRef } from 'react';
import { IFilterColumn } from './index';
import getFilterConditionRules from 'components/report-content/components/manage-filters/helpers/getFilterConditionRules';
import buildFilterColumns from 'components/report-content/components/manage-filters/helpers/buildFilterColumns';
import ReactSelect from 'components/common/react-select/ReactSelect';
import { Box, Button, Stack, Tooltip } from '@mui/material';
import { StaticAddon } from 'components/common/static-addon/StaticAddon';
import { ReportFilterSegment } from 'components/report-content/hooks/useReportContentSettings';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import { useEmbeddingContextVisible } from 'hooks/useEmbeddingContext';
import { externalReportAPI } from 'api/external-report';
import { reportAPI } from 'api/report';
import { FormComponentValue } from 'components/common/form/layout/control';
import ReportContentSkeletonPlaceholder from 'components/report-content/utils/ReportContentSkeletonPlaceholder';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import {
    ComponentSettingsManageFilters,
    ComponentUpdateProps,
    ManageFiltersInstance,
    IBlocksContext,
    BlocksContext,
    RendererFilter,
} from '@report-content';
import { useInitialData } from './helpers/useInitialData';
import { useFiltersRelevantValues } from './helpers/useFiltersRelevantValues';
import { useFilterRequestParams } from './helpers/useFilterRequestParams';
import ReportViewerReferenceManagerIcon from 'app/report/ReportViewerReferenceManagerIcon';
import PanelWrapper from 'components/report-content/layout/PanelWrapper';
import FilterControl from 'components/report-content/components/manage-filters/parts/filter/FilterControl';

// On 'viewer' mode send request to save selected values
export function saveFiltersValue(elementId: number, segmentValueId: number, settings: ComponentSettingsManageFilters) {
    const getCurrentFilters = (onlyChangedFilters = true) => {
        const savedFilters: any = settings.fields
            .filter((f) => {
                let canBeCached = true;
                return (
                    f.filter_column === 'Y' &&
                    (f.filterType === 'multiselect' || f.filterType === 'dropdown') &&
                    (!onlyChangedFilters || canBeCached)
                );
            })
            .map((f) => ({
                name: f.column_name,
                title: f.filter_label,
                filterType: f.filterType,
                referenceName: f.reference_name_escaped,
                values: f.savedValues,
                canBeCached: true,
            }));
        if (savedFilters.length > 0) {
            return savedFilters;
        }
        return null;
    };
    const filterData = getCurrentFilters();
    if (filterData) {
        externalReportAPI.bookmark.saveFilters(elementId, segmentValueId, filterData);
    }
}

// '' stays as is: an open Range bound or the blank value. A non-numeric Range bound becomes open,
// other non-numeric values are dropped.
function parseNumericFilterValues(values: Array<string>, filterType?: string): Array<number | string> {
    const result: Array<number | string> = [];
    values.forEach((v) => {
        if (v.trim() === '') {
            result.push('');
            return;
        }
        const parsed = Number(v);
        if (Number.isFinite(parsed)) {
            result.push(parsed);
        } else if (filterType == 'range') {
            result.push('');
        }
    });
    return result;
}

export default function ManageFilters({
    contentSettings,
    component,
    updateBlockSettings,
    actions,
    editPanel,
    blockId,
}: ComponentUpdateProps<ComponentSettingsManageFilters>) {
    const { t } = useBundleTranslation(['components/report-content']);
    const requestParams = useFilterRequestParams();
    const [filterColumns, setFilterColumns] = useState<Array<IFilterColumn>>([]);
    const [lastFilterSettings, setLastFilterSettings] = useState<any>(null);
    const { initialData, removeInitialData } = useInitialData(contentSettings, component);
    const { relevantValues, isLoadingRelevantValues } = useFiltersRelevantValues(
        contentSettings,
        component,
        filterColumns,
    );
    const getFilterSettings = (filters: Array<IFilterColumn>) => {
        return filters
            .slice()
            .sort((a, b) => (a.reference_name > b.reference_name ? 1 : -1))
            .map((filter) => ({
                name: filter.reference_name,
                type: filter.filterType,
                sort_order: filter.sortDirection,
                use_external_filter_value: filter.use_external_filter_value,
                external_delimiter: filter.external_delimiter,
                external_dataset_column_title_id: filter.external_dataset_column_title_id,
                external_dataset_column_value_id: filter.external_dataset_column_value_id,
                set_default_value: filter.set_default_value,
                filter_default_value: filter.filter_default_value,
                parent_reference_name: filter.parent_reference_name,
            }));
    };

    const [urlParamsParsed, setUrlParamsParsed] = useState(false);
    const filtersSetViaUrl = useRef<string[]>([]);
    // Pass filter values from URL GET params (or FilterRequestParamsContext override) into filters
    useEffect(() => {
        if (component.settings.fields) {
            component.settings.fields
                .filter((field) => field.filter_column == 'Y')
                .map((field) => {
                    for (const [key, value] of Object.entries(requestParams)) {
                        if (
                            key.toLowerCase() === field.reference_name.toLowerCase() ||
                            (field.column_name?.length &&
                                key.replace(/\s/g, '_').toLowerCase() ===
                                    field.column_name.replace(/\s/g, '_').toLowerCase()) ||
                            (field.filter_label?.length &&
                                key.replace(/\s/g, '_').toLowerCase() ===
                                    field.filter_label.replace(/\s/g, '_').toLowerCase())
                        ) {
                            field.savedValues =
                                field.value_type == 'numeric'
                                    ? parseNumericFilterValues(value, field.filterType)
                                    : value;
                            filtersSetViaUrl.current.push(field.reference_name);
                            break;
                        }
                    }
                });
        }
        setUrlParamsParsed(true);
    }, []);

    const blocksContext = useContext<IBlocksContext | null>(BlocksContext);

    const [filtersReset, setFiltersReset] = useState(false);
    useEffect(() => {
        const newFilterColumns = buildFilterColumns(component.settings, initialData);

        if (relevantValues) {
            newFilterColumns.forEach((filter) => {
                if (filter.filterType != 'dropdown' && filter.filterType != 'multiselect') {
                    return;
                }
                const allowed = relevantValues[filter.reference_name];
                if (!Array.isArray(allowed) || !Array.isArray(filter.data)) {
                    return;
                }
                const allowedSet = new Set(allowed.map((v) => String(v ?? '')));
                const selectedSet = new Set((filter.savedValues ?? []).map((v: any) => String(v ?? '')));
                filter.data = filter.data.filter(
                    (row: any) =>
                        allowedSet.has(String(row[filter.uid] ?? '')) || selectedSet.has(String(row[filter.uid] ?? '')),
                );
            });
        }

        const filterSettings = getFilterSettings(newFilterColumns);
        if (JSON.stringify(filterSettings) != JSON.stringify(lastFilterSettings)) {
            setLastFilterSettings(filterSettings);
            if (lastFilterSettings != null) {
                removeInitialData();
                return;
            }
        }
        let shouldResetData = false;
        const changedKeys = [
            'filterType',
            'use_external_filter_value',
            'external_delimiter',
            'external_dataset_column_title_id',
            'external_dataset_column_value_id',
        ] as const;

        if (filterColumns.length != newFilterColumns.length) {
            shouldResetData = true;
        } else {
            filterColumns.forEach((p) => {
                const newFilter = newFilterColumns.find((n) => n.reference_name == p.reference_name);
                if (!newFilter || changedKeys.some((k) => newFilter[k] != p[k])) {
                    shouldResetData = true;
                }
            });
        }

        if (!shouldResetData && blocksContext) {
            const rules = getFilterConditionRules(component, newFilterColumns);
            blocksContext.setFilters(rules);
        }

        let valuesWasChanged = false;
        newFilterColumns.forEach((filter) => {
            if (filter.data == '' || (filter.filterType != 'dropdown' && filter.filterType != 'multiselect')) {
                return;
            }

            const allowedList = Array.isArray(filter.data)
                ? filter.data.map((row: any) => {
                      return row[filter.uid] ?? '';
                  })
                : [];

            let newValues = filter.savedValues.filter((v) => allowedList.includes(v));
            if (urlParamsParsed && !filtersReset) {
                // Reset filter value if it was NOT set via URL, but at least one of them was.
                if (filtersSetViaUrl.current.length) {
                    if (!filtersSetViaUrl.current.includes(filter.reference_name)) {
                        if (filter.filterType == 'dropdown') {
                            newValues =
                                filter.include_all_value == 'Y' || filter.override_all_value == 'Y'
                                    ? []
                                    : (allowedList?.[0] ?? []);
                        } else {
                            newValues = [];
                        }
                    }
                }
                setFiltersReset(true);
            }

            if (JSON.stringify(newValues) != JSON.stringify(filter.savedValues)) {
                filter.savedValues = newValues;
                valuesWasChanged = true;
            }
        });

        if (valuesWasChanged) {
            const fields = component.settings.fields.slice();
            fields.forEach((f) => {
                const filter = newFilterColumns.find((filter) => filter.uid == f.reference_name_escaped);
                if (filter) {
                    f.savedValues = filter.savedValues;
                }
            });
            updateBlockSettings(
                { ...component.settings, fields: fields },
                { shouldDirty: blocksContext?.manageFiltersReady },
            );
        }
        setFilterColumns(newFilterColumns);
    }, [component.settings, initialData, urlParamsParsed, relevantValues]);

    useEffect(() => {
        if (!initialData || !blocksContext) {
            return;
        }
        setTimeout(() => blocksContext.setManageFiltersReady(true), 100);
    }, [initialData]);
    const isVisibleSegment = useEmbeddingContextVisible('segment');
    const isVisibleFilters = useEmbeddingContextVisible('eFilters');

    // Persists the given fields' savedValues to the viewer's bookmark and report-filter APIs.
    const persistFilterValues = (fields: Array<RendererFilter>) => {
        if (contentSettings.context != 'view') {
            return;
        }
        saveFiltersValue(contentSettings.elementId, contentSettings.segmentValueId, { ...component.settings, fields });
        const filtersState = fields
            .filter((f) => f.filter_column == 'Y')
            .map((f) => ({ referenceName: f.reference_name_escaped, values: f.savedValues }));
        reportAPI.saveReportFilters(contentSettings.elementId, contentSettings.segmentValueId, {
            fields: filtersState,
        });
    };

    const handleFilterChange = (filter: IFilterColumn, value: any) => {
        if (!component.settings.fields) {
            return;
        }
        const index = component.settings.fields.findIndex((f) => f.reference_name_escaped == filter.uid);

        if (index == -1) {
            return;
        }

        const fields = component.settings.fields.slice();
        fields[index] = { ...fields[index], savedValues: Array.isArray(value) ? value : [value] };
        persistFilterValues(fields);
        updateBlockSettings({ ...component.settings, fields: fields });
    };

    const handleResetFilters = () => {
        if (!component.settings.fields) {
            return;
        }
        // Range and charting_interval filters have no "all values" state to reset to, so leave them as-is.
        const fields = component.settings.fields.map((f) =>
            f.filterType == 'dropdown' || f.filterType == 'multiselect' ? { ...f, savedValues: [] } : f,
        );
        persistFilterValues(fields);
        updateBlockSettings({ ...component.settings, fields });
    };
    if (!initialData) {
        return (
            <Stack spacing={3} direction={'column'}>
                <ReportContentSkeletonPlaceholder height={54} />
                <ReportContentSkeletonPlaceholder height={600} />
            </Stack>
        );
    }

    // Hide whole filters block if no filters available
    const hasInst =
        contentSettings.hasInstances && contentSettings.instances.length && contentSettings.measurement_time;
    if (
        contentSettings.context == 'view' &&
        !(
            hasInst ||
            contentSettings.related.length > 0 ||
            (contentSettings.segmentValues.segments.length > 0 && isVisibleSegment) ||
            filterColumns.length
        )
    ) {
        return null;
    }

    const hasResettableFilterValues = filterColumns.some(
        (fc) => (fc.filterType == 'dropdown' || fc.filterType == 'multiselect') && fc.savedValues?.length > 0,
    );

    return (
        <Box>
            <Box
                className={'filter-block-scroll-overlap'}
                sx={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'background.default',
                    height: '32px',
                }}
            />
            {isVisibleFilters && (
                <Box sx={{ position: 'relative' }}>
                    <LoadingPlaceholder
                        relativeToParent
                        open={isLoadingRelevantValues}
                        sx={{
                            zIndex: 2,
                            borderRadius: 1,
                            '.loading-placeholder__icon': {
                                width: 24,
                                height: 24,
                            },
                        }}
                    />
                    <PanelWrapper>
                        <Box sx={{ display: 'flex', flexGrow: 1, alignItems: 'flex-start' }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', flexGrow: 1, alignItems: 'flex-start' }}>
                                {hasInst && (
                                    <Box sx={{ width: '264px', display: 'inline-flex', m: 0.75 }}>
                                        <StaticAddon sx={{ maxWidth: '110px' }}>
                                            <Tooltip
                                                placement="bottom-start"
                                                enterDelay={1000}
                                                title={t('snapshot_date')}
                                            >
                                                <span>{t('snapshot_date')}</span>
                                            </Tooltip>
                                        </StaticAddon>
                                        <Box sx={{ flexGrow: 1 }}>
                                            <InstanceSelect
                                                instances={contentSettings.instances}
                                                selected={contentSettings.measurement_time ?? ''}
                                                onChange={(value) => actions.updateMeasurementTime(value)}
                                            />
                                        </Box>
                                    </Box>
                                )}
                                {contentSettings.segmentValues.segments.length > 0 && isVisibleSegment && (
                                    <Box sx={{ width: '264px', display: 'inline-flex', m: 0.75 }}>
                                        <StaticAddon sx={{ maxWidth: '110px' }}>
                                            <Tooltip
                                                placement="bottom-start"
                                                enterDelay={1000}
                                                title={contentSettings.segmentValues.segments[0].name}
                                            >
                                                <span>{contentSettings.segmentValues.segments[0].name} </span>
                                            </Tooltip>
                                        </StaticAddon>
                                        <SegmentValueSelect
                                            segment={contentSettings.segmentValues.segments[0]}
                                            selected={String(contentSettings.segmentValueId)}
                                            onChange={(value) => actions.updateSegmentValue(value)}
                                        />
                                    </Box>
                                )}
                                {filterColumns
                                    .sort((a, b) => a.column_sequence - b.column_sequence)
                                    .map((fc) => (
                                        <FilterControl
                                            key={fc.uid}
                                            filter={fc}
                                            handleFilterChange={handleFilterChange}
                                        />
                                    ))}
                                {component.settings.only_display_relevant_values == 'Y' && (
                                    <Box sx={{ display: 'inline-flex', verticalAlign: 'top', m: 0.75 }}>
                                        <Tooltip title={t('manage_filters.reset_filters')}>
                                            <Box component={'span'}>
                                                <Button
                                                    data-test={'reset-filters-button'}
                                                    disabled={!hasResettableFilterValues}
                                                    onClick={handleResetFilters}
                                                    variant="outlined"
                                                    className={'min-width--icon'}
                                                >
                                                    <IconMi icon="rotate-ccw" fontSize="16" />
                                                </Button>
                                            </Box>
                                        </Tooltip>
                                    </Box>
                                )}
                                {contentSettings.context == 'view' && urlParamsParsed && (
                                    <Box sx={{ display: 'inline-flex', verticalAlign: 'top', mt: 0.75 }}>
                                        <ReportViewerReferenceManagerIcon
                                            actions={actions}
                                            initialData={initialData}
                                            contentSettings={contentSettings}
                                            component={component}
                                            updateBlockSettings={updateBlockSettings}
                                            setDefaultBookmark={filtersSetViaUrl.current.length == 0}
                                        />
                                    </Box>
                                )}
                            </Box>
                            {contentSettings.context == 'view' && contentSettings.related.length > 0 && (
                                <Box sx={{ width: '164px', display: 'inline-flex', m: 0.75, flexShrink: 0 }}>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <RelatedSelect
                                            data={[
                                                {
                                                    label: t('see_related', { count: contentSettings.related.length }),
                                                    value: '',
                                                    props: {
                                                        icon: {
                                                            type: 'mi',
                                                            value: 'elements-2',
                                                        },
                                                    },
                                                },
                                                ...contentSettings.related,
                                            ]}
                                            selected={''}
                                            onChange={(value: any) => {
                                                window.location.href = value;
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Box>
                        {contentSettings.context == 'edit' && (
                            <Box sx={{ m: 0.75 }}>
                                <Button
                                    sx={{ whiteSpace: 'nowrap' }}
                                    startIcon={<IconMi icon="gear" />}
                                    onClick={() => actions.openBlockEditor(String(blockId), editPanel)}
                                    data-test={'edit-filters-button'}
                                >
                                    {t('manage_filters.edit_filters')}
                                </Button>
                            </Box>
                        )}
                    </PanelWrapper>
                </Box>
            )}
        </Box>
    );
}

function InstanceSelect({
    instances,
    selected,
    onChange,
}: {
    instances: Array<ManageFiltersInstance>;
    selected: string;
    onChange: (values: string) => void;
}) {
    const data = instances.map((inst) => ({ label: inst.measurement_time, value: inst.measurement_time_raw }));

    return (
        <Box sx={{ flexGrow: 1 }}>
            <ReactSelect
                dataTest={'instance-value'}
                data={data}
                update={(value) => {
                    onChange(value);
                }}
                value={selected}
            />
        </Box>
    );
}

function SegmentValueSelect({
    segment,
    selected,
    onChange,
}: {
    segment: ReportFilterSegment;
    selected: string;
    onChange: (values: string) => void;
}) {
    const data = segment.values.map((v) => ({ label: v.segment, value: String(v.segment_value_id) }));

    return (
        <Box sx={{ flexGrow: 1 }}>
            <ReactSelect
                dataTest={'segment-value'}
                data={data}
                update={(value) => {
                    onChange(value);
                }}
                value={selected}
            />
        </Box>
    );
}

function RelatedSelect({
    data,
    selected,
    onChange,
}: {
    data: Array<FormComponentValue>;
    selected: string;
    onChange: (values: string) => void;
}) {
    return (
        <Box sx={{ flexGrow: 1 }}>
            <ReactSelect
                dataTest={'related-value'}
                data={data}
                update={(value) => {
                    onChange(value);
                }}
                value={selected}
            />
        </Box>
    );
}
